import { Button, FormControl, FormLabel, Heading, Input, Textarea } from "@chakra-ui/react";
import { ReactElement, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import api from "../../api";
import useInitSettings from "../../hooks/useInitSettings";
import useShowToast from "../../hooks/useShowToast";
import useSettingsStore from "../../store/settings";

type GeneralSettingsFormData = {
  weeks_number: number;
  weeks_history_number: number;
  cancel_time: number;
  announcement_text: string;
};

const SettingsGeneral = (): ReactElement => {
  const { t } = useTranslation();
  const [settings] = useSettingsStore(state => [state.settings]);
  const { refreshSettings } = useInitSettings(false);
  const { register, handleSubmit, reset } = useForm<GeneralSettingsFormData>();
  const { successToast, errorToast } = useShowToast();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!settings) {
      return;
    }

    reset({
      weeks_number: settings.weeks_number,
      weeks_history_number: settings.weeks_history_number,
      cancel_time: settings.cancel_time,
      announcement_text: settings.announcement_text,
    });
  }, [settings, reset]);

  const onSubmit = async (data: GeneralSettingsFormData) => {
    setIsLoading(true);
    const settings = await api.updateSettings({ ...data });
    if (!settings || api.error) {
      console.log("Error updating settings", api.error);
      setIsLoading(false);
      errorToast({
        title: t("settings.general.error_title"),
        description: t("settings.general.error_message.default"),
      });
      return;
    }

    refreshSettings();
    setIsLoading(false);
    successToast({
      title: t("settings.general.success_message"),
      description: t("settings.general.success_message"),
    });
  };

  return (
    <>
      <Heading size="lg" mb={4}>
        {t("settings.general.title")}
      </Heading>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl mb={4} isRequired>
          <FormLabel>{t("settings.general.weeks_number")}</FormLabel>
          <Input type="number" {...register("weeks_number", { valueAsNumber: true })} />
        </FormControl>
        <FormControl mb={4} isRequired>
          <FormLabel>{t("settings.general.weeks_history_number")}</FormLabel>
          <Input type="number" {...register("weeks_history_number", { valueAsNumber: true })} />
        </FormControl>
        <FormControl mb={4} isRequired>
          <FormLabel>{t("settings.general.cancel_time")}</FormLabel>
          <Input type="number" {...register("cancel_time", { valueAsNumber: true })} />
        </FormControl>
        <FormControl mb={6}>
          <FormLabel>{t("settings.general.announcement_text")}</FormLabel>
          <Textarea
            {...register("announcement_text")}
            placeholder={t("settings.general.announcement_text")}
          />
        </FormControl>

        <Button mt={6} colorScheme="blue" type="submit" isLoading={isLoading}>
          {t("settings.general.save")}
        </Button>
      </form>
    </>
  );
};

export default SettingsGeneral;
